// Auto-generated insight templates per spec p.47.
// Each builder returns { title, body, severity }.

function severityForLeakage(pct) {
  if (pct == null) return 'info';
  if (pct >= 50) return 'danger';
  if (pct >= 20) return 'warning';
  return 'good';
}

// Backend `lift_ratio` ships pre-rounded; derive from raw on/off percentages
// when both are present so the displayed ratio matches the bars in the card.
// Round on/off to whole percentages first so 86 / 82 → 1.0488 → 1.05 instead
// of the float-noise ratio (e.g. 85.6 / 82.3 → 1.04) the user wouldn't expect.
function searchLiftRatio(r) {
  const on = Math.round(Number(r?.search_on_pct));
  const off = Math.round(Number(r?.search_off_pct));
  if (Number.isFinite(on) && Number.isFinite(off) && off > 0) return on / off;
  return Number(r?.lift_ratio || 0);
}

// Format a lift ratio at two-decimal precision, with one special case: when
// rounding the value at 1-decimal precision cascades a carry into the integer
// (2.97 → 3.0 → "3.00", 7.988 → 8.0 → "8.00"), render the whole number with
// trailing ".00". Already-integer values render the same way ("3.00").
// Values that don't cross an integer boundary keep their hundredths intact
// (1.0488 → "1.05", 1.13 → "1.13", 3.50 → "3.50") — markup #107.
export function fmtLiftRatio(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  const oneDecimal = Number(v.toFixed(1));
  // Carry crossed the integer boundary, e.g. 2.97 → 3.0 or 7.988 → 8.0.
  if (Number.isInteger(oneDecimal) && oneDecimal > v) {
    return `${oneDecimal.toFixed(0)}.00`;
  }
  // Already whole (3.00, 1.00, etc.).
  if (Number.isInteger(v)) return `${v.toFixed(0)}.00`;
  return v.toFixed(2);
}

export function buildSearchLiftInsight(rows = []) {
  if (!Array.isArray(rows) || rows.length === 0) return null;
  const sorted = [...rows].sort((a, b) => searchLiftRatio(b) - searchLiftRatio(a));
  const top = sorted[0];
  if (!top) return null;
  return {
    title: 'Search lift',
    body: `Search-on lifts visibility most for ${top.provider_name || top.provider}: ${fmtLiftRatio(searchLiftRatio(top))}× more mentions when search is enabled.`,
    severity: 'info',
  };
}

// Pluralization for the "your top priority/priorities rank lower than AI's
// leading mentions" footer on the Amenity Gaps card. Returns null when there
// are no under-represented amenities; otherwise picks singular vs. plural
// grammar based on how many items are cited (markup #111).
export function buildAmenityUnderrepNarrative(parts) {
  if (!Array.isArray(parts) || parts.length === 0) return null;
  if (parts.length === 1) {
    return `Your top priority ranks lower than AI's leading mentions: ${parts[0]}. Emphasize it in your content so models surface it first.`;
  }
  return `Your top priorities rank lower than AI's leading mentions: ${parts.join('; ')}. Emphasize them in your content so models surface them first.`;
}

export function buildLeakageInsight(snapshot) {
  if (!snapshot || snapshot.value_numeric == null) return null;
  const pct = Number(snapshot.value_numeric);
  return {
    title: 'Booking direction leakage',
    body: `When AI tells searchers where to book, ${pct.toFixed(1)}% of the time it points them to a third party instead of you.`,
    severity: severityForLeakage(pct),
  };
}

export function buildCitationInsight(rows = []) {
  if (!Array.isArray(rows) || rows.length === 0) return null;
  const dyrt = rows.find((r) => /the[\s-]?dyrt/i.test(r.source || r.domain || ''));
  const own = rows.find((r) => r.is_client || r.is_own_site);
  if (dyrt && own) {
    const dyrtCount = Number(dyrt.citation_count || dyrt.count || 0);
    const ownCount = Number(own.citation_count || own.count || 0);
    // Only flag this as a warning when an OTA actually outranks the
    // client's own site. When the client is ahead the same information
    // is good news and should not be painted in alert colors.
    if (dyrtCount > ownCount) {
      return {
        title: 'Citation source',
        body: `The Dyrt is cited ${dyrtCount}× while your site is cited ${ownCount}× — your content is being routed through OTAs first.`,
        severity: 'warning',
      };
    }
    return {
      title: 'Citation source',
      body: `Your site is cited ${ownCount}× vs The Dyrt at ${dyrtCount}× — you lead the OTA in citations.`,
      severity: 'info',
    };
  }
  if (dyrt) {
    return {
      title: 'Citation source',
      body: `The Dyrt is the dominant cited source (${Number(dyrt.citation_count || dyrt.count || 0)}× citations).`,
      severity: 'warning',
    };
  }
  return null;
}

export function buildAmenityGapInsight(rows = []) {
  if (!Array.isArray(rows) || rows.length === 0) return null;
  const missing = rows.filter((r) => r.gap === 'missing' || r.ai_known === false);
  if (missing.length === 0) return null;
  const topMissRaw = missing[0].amenity || missing[0].name || '';
  // Strip the dev-shorthand "Num " prefix on priority keys (markup #101).
  const topMiss = String(topMissRaw).replace(/^Num\s+/i, '');
  return {
    title: 'Amenity gap',
    body: `${missing.length} amenities you offer are not mentioned by AI. Top miss: ${topMiss}.`,
    severity: 'warning',
  };
}

// Mirrors `bucketByMismatchScore` in GoogleOrganicTab.js so the insight count
// always equals the number of orange "Organic Strong / AI Weak" dots in the
// chart. Keep these definitions in sync.
function isOrganicStrongAiWeak(row) {
  const type = row.mismatch_type
    || (Number(row.mismatch_score ?? 0) >= 2 ? 'organic_strong_ai_weak' : null);
  return type === 'organic_strong_ai_weak';
}

export function buildOrganicMismatchInsight(rows = []) {
  if (!Array.isArray(rows) || rows.length === 0) return null;
  const opps = rows.filter(isOrganicStrongAiWeak);
  if (opps.length === 0) return null;
  const queryWord = opps.length === 1 ? 'query' : 'queries';
  return {
    title: 'Organic vs AI mismatch',
    body: `${opps.length} ${queryWord} flagged Organic Strong / AI Weak (orange dots): you rank well in Google but AI barely mentions you. These are your biggest AI optimization opportunities — you already have the SEO authority, you just need AI to see it.`,
    severity: opps.length >= 5 ? 'warning' : 'info',
  };
}

export function buildHallucinationWarning(count, providerCount) {
  if (!count) return null;
  return {
    title: 'Hallucinations detected',
    body: `${count} hallucinated facts were detected across ${providerCount || '—'} providers this run.`,
    severity: count >= 5 ? 'danger' : 'warning',
  };
}

export function buildAioExpansionInsight(triggerPct) {
  if (triggerPct == null) return null;
  const pct = Number(triggerPct);
  return {
    title: 'AI Overviews footprint',
    body: `AI Overviews are now triggered on ${pct.toFixed(1)}% of monitored queries.`,
    severity: pct >= 30 ? 'warning' : 'info',
  };
}
